import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { Button, Tag } from "../../../../design-system";
import { BoardStageDef, BoardStageTone } from "../../../../types/Board";
import { setBoardStages } from "../../../../database/board/boardApi";
import { TONE_DOT } from "../../outreach/components/leadUtils";
import {
  ActionsRow,
  Backdrop,
  CloseBtn,
  ErrorText,
  ModalBody,
  ModalCard,
  ModalFoot,
  ModalHead,
  ModalSub,
  ModalTitle,
  slugify,
} from "./modalKit";

// Operator-only editor for the board's workflow states. Rows are grouped with
// hairlines (no card-per-row chrome), the label is a borderless inline input,
// the key auto-slugs from the label and stays out of the way as subtle mono
// text, and the tone is picked by tapping a swatch instead of a select.

const STAGE_TONES: BoardStageTone[] = [
  "neutral",
  "info",
  "accent",
  "success",
  "warning",
  "danger",
];

const Rows = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-1);
  border-bottom: 1px solid var(--color-border-subtle);

  &:last-child {
    border-bottom: none;
  }
`;

const ReorderCol = styled.div`
  display: flex;
  flex-direction: column;
  opacity: 0.45;
  transition: opacity var(--duration-fast) var(--ease-standard);

  ${Row}:hover & {
    opacity: 1;
  }
`;

const ChevronBtn = styled.button`
  border: none;
  background: transparent;
  color: var(--color-text-tertiary);
  width: 20px;
  height: 14px;
  line-height: 1;
  font-size: 9px;
  cursor: pointer;
  padding: 0;
  border-radius: var(--radius-sm);

  &:hover:not(:disabled) {
    color: var(--color-text);
  }
  &:active:not(:disabled) {
    transform: translateY(1px);
  }
  &:disabled {
    opacity: 0.25;
    cursor: default;
  }
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const LabelInput = styled.input`
  border: none;
  background: transparent;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  padding: 2px 0;
  border-bottom: 1px solid transparent;
  transition: border-color var(--duration-fast) var(--ease-standard);
  min-width: 0;

  &::placeholder {
    color: var(--color-text-tertiary);
    font-weight: var(--font-weight-regular, 400);
  }
  &:focus {
    outline: none;
    border-bottom-color: var(--color-accent);
  }
`;

const KeyLine = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
`;

const KeyInput = styled.input`
  border: none;
  background: transparent;
  font-family: var(--font-mono, monospace);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  padding: 0;
  width: 160px;
  border-bottom: 1px solid transparent;
  transition: border-color var(--duration-fast) var(--ease-standard),
    color var(--duration-fast) var(--ease-standard);

  &::placeholder {
    color: var(--color-text-tertiary);
    opacity: 0.6;
  }
  &:hover {
    color: var(--color-text-secondary);
  }
  &:focus {
    outline: none;
    color: var(--color-text-secondary);
    border-bottom-color: var(--color-border-strong, var(--color-border));
  }
`;

const Swatches = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
`;

const Swatch = styled.button<{ $c: string; $active: boolean }>`
  width: 14px;
  height: 14px;
  border-radius: var(--radius-full);
  background: ${(p) => p.$c};
  border: none;
  padding: 0;
  cursor: pointer;
  opacity: ${(p) => (p.$active ? 1 : 0.35)};
  transition: opacity var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-fast) var(--ease-standard),
    transform var(--duration-fast) var(--ease-standard);

  ${(p) =>
    p.$active &&
    css`
      box-shadow: 0 0 0 2px var(--color-surface), 0 0 0 3.5px ${p.$c};
    `}

  &:hover {
    opacity: 1;
  }
  &:active {
    transform: scale(0.9);
  }
`;

const RemoveBtn = styled.button`
  border: none;
  background: transparent;
  color: var(--color-text-tertiary);
  width: 26px;
  height: 26px;
  line-height: 1;
  font-size: 15px;
  cursor: pointer;
  padding: 0;
  border-radius: var(--radius-sm);
  opacity: 0.45;
  transition: opacity var(--duration-fast) var(--ease-standard),
    color var(--duration-fast) var(--ease-standard),
    background var(--duration-fast) var(--ease-standard);

  ${Row}:hover & {
    opacity: 1;
  }

  &:hover {
    color: var(--color-danger);
    background: var(--color-danger-subtle, var(--color-surface-subtle));
  }
`;

const AddRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  width: 100%;
  margin-top: var(--space-3);
  padding: var(--space-2);
  border: 1px dashed var(--color-border);
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--color-text-tertiary);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  cursor: pointer;
  transition: border-color var(--duration-fast) var(--ease-standard),
    color var(--duration-fast) var(--ease-standard);

  &:hover {
    border-color: var(--color-accent);
    color: var(--color-text-secondary);
  }
`;

const StageEditorModal: React.FC<{
  initial: BoardStageDef[];
  cardCounts: Record<string, number>;
  onClose: () => void;
}> = ({ initial, cardCounts, onClose }) => {
  const [rows, setRows] = useState<BoardStageDef[]>(() =>
    initial.map((s) => ({ ...s }))
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  const patch = (i: number, next: Partial<BoardStageDef>) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...next } : r)));

  const move = (i: number, dir: -1 | 1) =>
    setRows((rs) => {
      const j = i + dir;
      if (j < 0 || j >= rs.length) return rs;
      const copy = [...rs];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });

  const remove = (i: number) => {
    const row = rows[i];
    const count = cardCounts[row.key] || 0;
    if (
      count > 0 &&
      !window.confirm(
        `${count} card${count === 1 ? "" : "s"} on "${
          row.label || row.key
        }". Deleting the state keeps those cards but moves them to an archived column until you re-stage them. Delete anyway?`
      )
    )
      return;
    setRows((rs) => rs.filter((_, idx) => idx !== i));
  };

  const add = () =>
    setRows((rs) => [...rs, { key: "", label: "", tone: "neutral" }]);

  const save = async () => {
    setError(null);
    const normalized: BoardStageDef[] = rows.map((r) => ({
      key: slugify(r.key || r.label),
      label: r.label.trim(),
      tone: r.tone || "neutral",
    }));
    for (const r of normalized) {
      if (!r.label) return setError("Every state needs a label.");
      if (!/^[a-z0-9_]+$/.test(r.key))
        return setError(`"${r.label}" has an invalid key.`);
    }
    const keys = normalized.map((r) => r.key);
    const dupe = keys.find((k, i) => keys.indexOf(k) !== i);
    if (dupe) return setError(`Duplicate state key "${dupe}".`);
    if (!normalized.length) return setError("Keep at least one state.");

    setSaving(true);
    try {
      await setBoardStages(normalized);
      onClose();
    } catch (e: any) {
      setError(e?.message || "Could not save states.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Backdrop onClick={() => !saving && onClose()}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <ModalHead>
          <div>
            <ModalTitle>Workflow states</ModalTitle>
            <ModalSub>
              Top to bottom is the board's column order. Cards on a deleted
              state move to an archived column.
            </ModalSub>
          </div>
          <CloseBtn aria-label="Close" onClick={() => !saving && onClose()}>
            ×
          </CloseBtn>
        </ModalHead>

        <ModalBody>
          <Rows>
            {rows.map((r, i) => {
              const count = cardCounts[r.key] || 0;
              return (
                <Row key={i}>
                  <ReorderCol>
                    <ChevronBtn
                      type="button"
                      disabled={i === 0}
                      onClick={() => move(i, -1)}
                      aria-label="Move up"
                    >
                      ▲
                    </ChevronBtn>
                    <ChevronBtn
                      type="button"
                      disabled={i === rows.length - 1}
                      onClick={() => move(i, 1)}
                      aria-label="Move down"
                    >
                      ▼
                    </ChevronBtn>
                  </ReorderCol>

                  <Fields>
                    <LabelInput
                      value={r.label}
                      placeholder="State name"
                      onChange={(e) => patch(i, { label: e.target.value })}
                    />
                    <KeyLine>
                      <KeyInput
                        value={r.key}
                        placeholder={slugify(r.label) || "key"}
                        onChange={(e) =>
                          patch(i, { key: slugify(e.target.value) })
                        }
                        title="State key stored on cards (lowercase, no spaces)"
                      />
                      {count > 0 && (
                        <Tag variant="neutral" size="sm">
                          {count} card{count === 1 ? "" : "s"}
                        </Tag>
                      )}
                    </KeyLine>
                  </Fields>

                  <Swatches role="radiogroup" aria-label="State color">
                    {STAGE_TONES.map((t) => (
                      <Swatch
                        key={t}
                        type="button"
                        $c={TONE_DOT[t]}
                        $active={(r.tone || "neutral") === t}
                        title={t}
                        aria-label={t}
                        onClick={() => patch(i, { tone: t })}
                      />
                    ))}
                  </Swatches>

                  <RemoveBtn
                    type="button"
                    onClick={() => remove(i)}
                    aria-label="Delete state"
                  >
                    ×
                  </RemoveBtn>
                </Row>
              );
            })}
          </Rows>

          <AddRow type="button" onClick={add}>
            + Add state
          </AddRow>
          {error && <ErrorText>{error}</ErrorText>}
        </ModalBody>

        <ModalFoot>
          <ModalSub>
            {rows.length} state{rows.length === 1 ? "" : "s"}
          </ModalSub>
          <ActionsRow>
            <Button
              variant="ghost"
              size="sm"
              disabled={saving}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              isLoading={saving}
              onClick={save}
            >
              Save states
            </Button>
          </ActionsRow>
        </ModalFoot>
      </ModalCard>
    </Backdrop>
  );
};

export default StageEditorModal;
